// Sound Map — a hand-authored phonetics cheat sheet, styled like a note jotted on
// paper. Every letter/digraph → plain-English sound → IPA → an example you can
// hear. Hand-authored on purpose: sound mapping is exactly the thing an AI image
// generator gets wrong.
import { speakButtonHTML } from "../lib/audio.js";

const GROUPS = [
  {
    title: "Vowels — pure & constant",
    note: "Five clean sounds that never change. Master these first.",
    color: "text-emerald-700",
    rows: [
      ["a", "“ah” as in father", "/a/", "casa"],
      ["e", "“eh” as in bed", "/e/", "bene"],
      ["i", "“ee” as in see", "/i/", "vino"],
      ["o", "“oh” as in or", "/o/", "sole"],
      ["u", "“oo” as in food", "/u/", "luna"],
    ],
  },
  {
    title: "Consonants unlike English",
    note: "Where beginners slip — the rest sound close to English.",
    color: "text-amber-700",
    rows: [
      ["c + e/i", "soft “ch” as in chair", "/tʃ/", "cena"],
      ["c + a/o/u", "hard “k”", "/k/", "casa"],
      ["g + e/i", "soft “g” as in gem", "/dʒ/", "gelato"],
      ["g + a/o/u", "hard “g” as in go", "/ɡ/", "gatto"],
      ["h", "always silent — never pronounced", "—", "ho"],
      ["r", "tapped; trilled when doubled", "/r/", "Roma"],
      ["s", "“s”, or “z” between vowels", "/s/ /z/", "rosa"],
      ["z", "“ts” or “dz”", "/ts/ /dz/", "zio"],
    ],
  },
  {
    title: "Digraphs & special combos",
    note: "Two letters making one sound — the heart of Italian spelling.",
    color: "text-sky-700",
    rows: [
      ["ch + e/i", "hard “k” (keeps c hard)", "/k/", "chi"],
      ["gh + e/i", "hard “g” (keeps g hard)", "/ɡ/", "spaghetti"],
      ["gli", "“lli” as in million", "/ʎ/", "figlio"],
      ["gn", "“ny” as in canyon", "/ɲ/", "gnocchi"],
      ["sc + e/i", "“sh” as in ship", "/ʃ/", "pesce"],
      ["sc + a/o/u", "hard “sk”", "/sk/", "scuola"],
    ],
  },
];

function groupHTML(g) {
  const rows = g.rows
    .map(
      ([letter, sound, ipa, ex]) => `
      <div class="flex items-baseline gap-3 py-1.5 border-b border-dashed border-amber-300/50 last:border-0">
        <span class="font-bold ${g.color} text-xl min-w-[3.4rem]">${letter}</span>
        <span class="flex-1 text-sage-700">${sound}</span>
        <span class="text-xs text-sage-400 whitespace-nowrap" style="font-family:ui-monospace,monospace">${ipa}</span>
        <span class="font-semibold text-sage-800 min-w-[4.5rem] text-right">${ex}</span>${speakButtonHTML(ex)}
      </div>`
    )
    .join("");
  return `
    <div class="mb-6 last:mb-0">
      <h3 class="text-2xl ${g.color} sketch-underline inline-block mb-1">${g.title}</h3>
      <p class="text-sm text-sage-500 mb-2" style="font-family:Inter,sans-serif">${g.note}</p>
      <div>${rows}</div>
    </div>`;
}

export function soundMapView(app) {
  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <div class="flex items-center gap-2 mb-1">
        <h2 class="text-4xl font-script text-sage-800">Sound Map</h2><span class="text-2xl">🔊</span>
      </div>
      <p class="text-sage-600 mb-4" style="font-family:Inter,sans-serif">Italian is spelt as it sounds — learn these and you can pronounce almost any word on sight. Tap 🔊 to hear each example.</p>
      <div class="paper sketch-box p-5 sm:p-7 font-hand text-lg">
        ${GROUPS.map(groupHTML).join("")}
      </div>
      <p class="text-xs text-sage-400 mt-4" style="font-family:Inter,sans-serif">Double consonants are held longer, and the length carries meaning: <b>nonno</b> (grandad) vs <b>nono</b> (ninth), <b>sette</b> (seven) vs <b>sete</b> (thirst). Always give them their full weight.</p>
    </section>`;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
